/**
 * What every Atlas Intelligence tab shares: the page state (the design keeps it in one
 * state object, so switching tabs keeps the selection, the sliders and the sub-views),
 * the deal book, and the design's repeated style strings.
 */
import type { MouseEvent, ReactNode } from 'react';
import type { ApiClient } from '@aithora/shared';
import { useQuery, type QueryState } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { intelBreakdown, type CatalogBreakdown, type ProspectsResponse } from '@aithora/shared';
import type { Deal, MapSubject } from './model';

export type IntelTab =
  | 'find'
  | 'relmap'
  | 'northstar'
  | 'target'
  | 'deal'
  | 'scenario'
  | 'simulate'
  | 'bench'
  | 'track'
  | 'portfolio'
  | 'atlasq';

/** Every tab's name (V4 aiVals TABS). Scenario and Paper Trade live under Simulate as Levers and Track live. */
export const TAB_LABEL: Record<IntelTab, string> = {
  find: 'Find',
  relmap: 'Relationship Map',
  northstar: 'North Star',
  target: 'Analysis',
  scenario: 'Scenario',
  simulate: 'Deal what-if',
  deal: 'Simulate',
  portfolio: 'Portfolio',
  track: 'Tracking',
  bench: 'Benchmarks',
  atlasq: 'Atlas',
};

/** The V4 tab titles (hover text on the sub-tabs). */
export const TAB_TITLE: Partial<Record<IntelTab, string>> = {
  find: 'Every facility, ranked by what we can do for it',
  relmap: 'Who is connected to whom',
  northstar: 'Market map, ranked lists, distress, operators',
  target: 'Full deal sheet',
  scenario: 'What-if sliders and deal levers',
  simulate: 'Forecast and back-test',
  deal: 'Buy the business, the building, or both',
  bench: 'How it compares with its peers',
  track: 'Six-stage acquisition board',
  portfolio: 'What we hold, rolled up',
};

export type IntelGroupId = 'market' | 'facility' | 'deals';
export interface IntelGroup {
  id: IntelGroupId;
  label: string;
  tabs: IntelTab[];
  note: string;
}

/** The three sections of the V4 page (aiVals GROUPS). */
export const INTEL_GROUPS: IntelGroup[] = [
  { id: 'market', label: 'Market', tabs: ['find', 'relmap', 'northstar'], note: 'Across the whole market, not one building' },
  { id: 'facility', label: 'Facility', tabs: ['target', 'deal', 'bench'], note: 'All three work on the facility shown on the right' },
  { id: 'deals', label: 'Deals', tabs: ['track', 'portfolio'], note: 'What we are pursuing and what we hold' },
];

/**
 * Sales sees Find and the Relationship Map only (Andrew, 2026-10-01, decision 4), as one
 * Market section, with no Ask Atlas and no deal model. The server refuses Sales every
 * other section's routes (internal-access-policy SALES_INTEL_ROUTES).
 */
export const SALES_INTEL_TABS: readonly IntelTab[] = ['find', 'relmap'];
export const SALES_GROUPS: IntelGroup[] = [
  { id: 'market', label: 'Market', tabs: ['find', 'relmap'], note: 'Sales view: find facilities and see who they are connected to' },
];

/** Every tab key, for reading ?tab= (the V3 keys source and coverage now land on Find). */
export const ALL_TABS = Object.keys(TAB_LABEL) as IntelTab[];

/** The page state, the design's own keys. Absent means the design's default. */
export interface IntelState {
  aiSel?: number;
  /** V9: which "i" explainers are open (design v9i), so one stays open across a tab switch. */
  v9i?: Record<string, boolean>;
  /** The section last opened, and the tab last opened in each section (V4 aiLastGroup, aiLastTab). */
  aiLastGroup?: IntelGroupId;
  aiLastTab?: Partial<Record<IntelGroupId, IntelTab>>;
  /** The tab Ask Atlas goes back to. */
  aiPrevTab?: IntelTab;
  /** The facility picker on the section bar. */
  aiPickOpen?: boolean;
  aiPickQ?: string;
  /** A building carried over from the map or Find into the model (V4 aiMapSubj). */
  aiMapSubj?: MapSubject | null;
  nsView?: string;
  nsLens?: string;
  /** North Star > Operators: the owner search (GET /operators?q=). */
  nsOpQ?: string;
  /** North Star > Operators: the state filter and the sort (GET /operators?state=&sort=). */
  nsOpState?: string;
  nsOpSort?: string;
  ddTab?: string;
  ptMode?: string;
  ptRoi?: boolean;
  aiEval?: string;
  aiMode?: string;
  aiFilter?: string;
  aiSort?: string;
  aiQuery?: string;
  aiOccLift?: number;
  aiMixShift?: number;
  aiAgencyCut?: number;
  aiMedCut?: number;
  aiOccDrop?: number;
  aiMandate?: boolean;
  aiRun?: string;
  aiCms?: string;
  scOcc?: number;
  scMix?: number;
  scMargin?: number;
  scMult?: number;
  scAppetite?: string;
  scLevers?: Record<number, boolean>;
  srcFilt?: string;
  covSeg?: string;
  covState?: string;
  covQ?: string;
  bmType?: string;
  bmBed?: string;
  bmState?: string;
  /** Each view may keep more of its own keys here (typed in its own file). */
  [key: string]: unknown;
}

export interface TabProps {
  st: IntelState;
  set: (patch: IntelState) => void;
  client: ApiClient;
  book: QueryState<ProspectsResponse>;
  deals: Deal[];
  /** The selected facility (design: all[min(aiSel, n-1)]), or null while the book is empty. */
  f: Deal | null;
  sel: number;
  readOnly: boolean;
  /** Sales: Find and the Relationship Map only, no deal book, Deal Desk or paper book. */
  sales?: boolean;
  /** Open another tab (as the design's setState({ aiTab })), with an optional state patch. */
  go: (tab: IntelTab, patch?: IntelState) => void;
}

/** The design's `set(p)` click handler: no navigation, then the state patch. */
export const onSet = (set: (p: IntelState) => void, p: IntelState) => (e: MouseEvent) => {
  e.preventDefault();
  set(p);
};

/** The design's link-styled control (an anchor with href="#"). */
export function A({ style, onClick, children, title, disabled, label, hover }: { style: string; onClick?: (e: MouseEvent) => void; children: ReactNode; title?: string; disabled?: boolean; label?: string; hover?: string }) {
  return (
    <a
      href="#"
      role="button"
      className={hover}
      title={title}
      aria-label={label}
      aria-disabled={disabled || undefined}
      onClick={(e) => {
        e.preventDefault();
        if (!disabled) onClick?.(e);
      }}
      style={sx(style + (disabled ? 'opacity:.55;cursor:not-allowed;' : ''))}
    >
      {children}
    </a>
  );
}

// The design's repeated strings.
export const CARD = 'box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:15px 17px;';
export const CAP = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
export const CAP9 = 'font-size:14px;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#54606C;font-weight:600;';
export const MOD_TAG = 'font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;color:#54606C;background:rgba(34,39,46,.12);border-radius:4px;padding:2px 6px;';
export const ROW13 = 'display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:6px;font-size:16px;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.06);';

/** The chip toggle (aiNsVals chip()). */
export const chip = (on: boolean) =>
  'display:inline-flex;align-items:center;height:28px;padding:0 13px;border-radius:8px;font-size:14px;text-decoration:none;white-space:nowrap;' +
  (on ? 'background:rgba(79,138,214,.15);color:#2F5C97;font-weight:600;border:1px solid rgba(47,92,151,.34);' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);');

/** A grade tile (22px, 6px radius). */
export const gradeTile = (bg: string, size = 22, radius = 6) =>
  `display:inline-flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;border-radius:${radius}px;font-size:14px;font-weight:700;color:#fff;background:${bg};`;

/** A width bar (design bars()). */
export const bars = (val: number, max: number, color: string) =>
  'height:6px;border-radius:3px;background:' + color + ';width:' + Math.max(2, Math.min(100, (val / max) * 100)) + '%;';

/** A missing value, the console's way (never an em-dash). */
export const DASH = '–';

/** The section label with a rule after it (Scenario). */
export function SectionRule({ label }: { label: string }) {
  return (
    <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:5px;margin-top:6px;margin-bottom:2px;')}>
      <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.15em;text-transform:uppercase;color:#54606C;font-weight:700;white-space:nowrap;')}>{label}</span>
      <span style={sx('flex:1 1 60px;min-width:40px;height:1px;background:rgba(34,39,46,.1);')} />
    </div>
  );
}

/** A label / value row (Deal Desk, Returns, Concentration). */
export function KV({ label, val, style = ROW13, valStyle = 'font-variant-numeric:tabular-nums;font-weight:600;' }: { label: ReactNode; val: ReactNode; style?: string; valStyle?: string }) {
  return (
    <div style={sx(style)}>
      <span style={sx('color:#3C454F;')}>{label}</span>
      <span style={sx('flex:1;')} />
      <span style={sx(valStyle)}>{val}</span>
    </div>
  );
}

/** The design's stat tile (label, figure, note). */
export function Tile({ label, val, note, valStyle = 'font-size:20px;font-weight:700;letter-spacing:-.02em;', noteStyle = 'font-size:14px;color:#54606C;', pad = '13px 15px' }: { label: string; val: ReactNode; note: ReactNode; valStyle?: string; noteStyle?: string; pad?: string }) {
  return (
    <div style={sx(`box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:${pad};`)}>
      <div style={sx(CAP + 'margin-bottom:5px;')}>{label}</div>
      <div style={sx(valStyle)}>{val}</div>
      <div style={sx(noteStyle)}>{note}</div>
    </div>
  );
}

/** Thousands with commas (the design's toLocaleString in en-US). */
export const n0 = (x: number) => Math.round(x).toLocaleString('en-US');

/** "276,000+": a catalog count shown the design's way, rounded down to the thousand. */
export const plusK = (x: number) => (x >= 1000 ? (Math.floor(x / 1000) * 1000).toLocaleString('en-US') + '+' : String(x));

/** GET /api/financial/facilities/breakdown: the catalog total and its split by type. */
export function useBreakdown(client: ApiClient): QueryState<CatalogBreakdown> {
  return useQuery<CatalogBreakdown>((signal) => intelBreakdown(client, { signal }), [client]);
}

/** A bucket's count from the breakdown (0 when absent). */
export const bucket = (list: { key: string; count: number }[] | undefined, key: string) => list?.find((b) => b.key.toUpperCase() === key.toUpperCase())?.count ?? 0;
